// SINGLE SOURCE OF TRUTH for all localization data — imported by BOTH the Vite/TS app
// (config.tsx, messages.ts, trades.ts, geo.ts) AND the Node prerender/worker (prerender.mjs,
// worker.js). Plain JS so Node can import it directly; types live in data.d.mts.
//
// Each locale is ONE self-contained entry: a message catalog (native freight-desk copy, not MT),
// localized trade labels, and a country "gateway" (the money-page slug + the destination ports whose
// lanes get a localized variant + hreflang alternate). English lives at the site root; every other
// locale is a subdirectory. To add a language: append its LOCALES row, CATALOG[code], TRADES_I18N[code]
// and GATEWAYS[code]. Nothing else changes. Interpolation placeholders ({name}, {pol}, {price}, …)
// and container/Incoterm codes MUST be preserved verbatim across languages.

export const DEFAULT_LOCALE = 'en'

// code · htmlLang · hreflang · prefix ('' = root, English) · native label. Every EU official language
// (+ Norwegian) that has meaningful ex-Asia ocean-freight search demand. Irish (ga) and Maltese (mt)
// are omitted deliberately — those markets search in English, which is already served at the root.
const L = (code, label) => ({ code, htmlLang: code, hreflang: code, prefix: `/${code}`, label })
export const LOCALES = [
  { code: 'en', htmlLang: 'en', hreflang: 'en', prefix: '', label: 'English' },
  L('de', 'Deutsch'),
  L('fr', 'Français'),
  L('es', 'Español'),
  L('it', 'Italiano'),
  L('nl', 'Nederlands'),
  L('pt', 'Português'),
  L('pl', 'Polski'),
  L('sv', 'Svenska'),
  L('da', 'Dansk'),
  L('fi', 'Suomi'),
  L('no', 'Norsk'),
  L('el', 'Ελληνικά'),
  L('cs', 'Čeština'),
  L('ro', 'Română'),
  L('hu', 'Magyar'),
  L('sk', 'Slovenčina'),
  L('bg', 'Български'),
  L('hr', 'Hrvatski'),
  L('sl', 'Slovenščina'),
  L('lt', 'Lietuvių'),
  L('lv', 'Latviešu'),
  L('et', 'Eesti'),
  L('tr', 'Türkçe'),
  L('uk', 'Українська'),
]

// Trade-lane labels per locale (join key = FreighTower line_category_id). English base lives in
// data/trades.ts; only non-English overrides go here.
const TRADES_BASE = {
  de: {
    18843: { name: 'Asien nach Europa & Mittelmeer', short: 'Asien–Europa', blurb: 'Spotraten von China und Asien nach Nordeuropa (Rotterdam, Hamburg, Antwerpen, Felixstowe) und ins Mittelmeer.' },
    18842: { name: 'Asien in die USA & nach Kanada', short: 'Asien–Nordamerika', blurb: 'Transpazifische Spotraten von Asien zu Häfen in den USA und Kanada (Los Angeles, Long Beach, New York, Vancouver).' },
    18844: { name: 'Asien nach Lateinamerika', short: 'Asien–Lateinamerika', blurb: 'Spotraten von Asien nach Lateinamerika (Santos, Callao, Manzanillo, Buenaventura).' },
    18849: { name: 'Innerasien', short: 'Innerasien', blurb: 'Kurzstrecken-Spotraten innerhalb Asiens und auf dem indischen Subkontinent.' },
    18845: { name: 'Asien nach Australien & Neuseeland', short: 'Asien–Ozeanien', blurb: 'Spotraten von Asien nach Australien und Neuseeland (Sydney, Melbourne, Auckland).' },
    18851: { name: 'Asien nach Afrika', short: 'Asien–Afrika', blurb: 'Spotraten von Asien zu afrikanischen Häfen (Durban, Lagos, Mombasa, Daressalam).' },
  },
}

// Country money-page config per locale. slug = localized landing path (under the locale prefix).
// podCodes = destination UN/LOCODEs whose lanes get a localized variant + hreflang. Empty podCodes
// (landlocked markets) → UI + country page only, no per-lane variants.
export const GATEWAYS = {
  de: { country: 'Germany', slug: 'seefrachtraten-asien-deutschland', podCodes: ['DEHAM', 'DEBRV', 'DEWVN', 'NLRTM', 'BEANR', 'BEZEE'] },
  fr: { country: 'France', slug: 'tarifs-fret-maritime-asie-france', podCodes: ['FRLEH', 'FRFOS', 'FRMRS', 'FRDKK'] },
  es: { country: 'Spain', slug: 'tarifas-flete-maritimo-asia-espana', podCodes: ['ESVLC', 'ESBCN', 'ESALG', 'ESBIO'] },
  it: { country: 'Italy', slug: 'tariffe-trasporto-marittimo-asia-italia', podCodes: ['ITGOA', 'ITSPE', 'ITNAP', 'ITTRS', 'ITGIT'] },
  nl: { country: 'Netherlands', slug: 'zeevrachttarieven-azie-nederland', podCodes: ['NLRTM', 'NLAMS'] },
  pt: { country: 'Portugal', slug: 'tarifas-frete-maritimo-asia-portugal', podCodes: ['PTLIS', 'PTLEI', 'PTSIN'] },
  pl: { country: 'Poland', slug: 'stawki-frachtu-morskiego-azja-polska', podCodes: ['PLGDN', 'PLGDY'] },
  sv: { country: 'Sweden', slug: 'sjofraktpriser-asien-sverige', podCodes: ['SEGOT'] },
  da: { country: 'Denmark', slug: 'soefragtrater-asien-danmark', podCodes: ['DKAAR', 'DKCPH'] },
  fi: { country: 'Finland', slug: 'merirahtihinnat-aasia-suomi', podCodes: ['FIHEL', 'FIHKO'] },
  no: { country: 'Norway', slug: 'sjofraktrater-asia-norge', podCodes: ['NOOSL'] },
  el: { country: 'Greece', slug: 'naula-thalassias-metaforas-asia-ellada', podCodes: ['GRPIR', 'GRSKG'] },
  cs: { country: 'Czechia', slug: 'sazby-namorni-prepravy-asie-cesko', podCodes: ['DEHAM', 'NLRTM', 'SIKOP', 'PLGDN'] },
  ro: { country: 'Romania', slug: 'tarife-transport-maritim-asia-romania', podCodes: ['ROCND'] },
  hu: { country: 'Hungary', slug: 'tengeri-fuvardijak-azsia-magyarorszag', podCodes: ['SIKOP', 'HRRJK', 'ROCND', 'DEHAM'] },
  sk: { country: 'Slovakia', slug: 'sadzby-namornej-prepravy-azia-slovensko', podCodes: ['SIKOP', 'DEHAM', 'NLRTM', 'HRRJK'] },
  bg: { country: 'Bulgaria', slug: 'ceni-morski-navla-azia-balgaria', podCodes: ['BGVAR', 'BGBOJ'] },
  hr: { country: 'Croatia', slug: 'pomorske-vozarine-azija-hrvatska', podCodes: ['HRRJK'] },
  sl: { country: 'Slovenia', slug: 'pomorske-prevoznine-azija-slovenija', podCodes: ['SIKOP'] },
  lt: { country: 'Lithuania', slug: 'juru-frachto-tarifai-azija-lietuva', podCodes: ['LTKLJ'] },
  lv: { country: 'Latvia', slug: 'juras-fraktu-likmes-azija-latvija', podCodes: ['LVRIX'] },
  et: { country: 'Estonia', slug: 'mereveo-hinnad-aasia-eesti', podCodes: ['EETLL'] },
  tr: { country: 'Turkey', slug: 'deniz-navlun-fiyatlari-asya-turkiye', podCodes: ['TRAMB', 'TRMER', 'TRIZM', 'TRGEB', 'TRIST'] },
  uk: { country: 'Ukraine', slug: 'morskyi-frakht-aziya-ukraina', podCodes: ['UAODS', 'UACHR', 'UAILK', 'UAPIV', 'ROCND', 'PLGDN'] },
}

// ── Message catalogs ─────────────────────────────────────────────────────────
const en = {
  // Header / nav
  'nav.rateIndex': 'Rate Index',
  'nav.lanes': 'Lanes',
  'nav.advertise': 'Advertise',
  'nav.pricing': 'Pricing',
  'nav.signIn': 'Sign in',
  'nav.account': 'Account',
  'nav.getQuote': 'Get a quote',
  'nav.theme': 'Hero theme',
  'nav.light': 'Light',
  'nav.dark': 'Dark',
  'nav.language': 'Language',
  // Footer
  'footer.tagline': 'Indicative ocean spot rates from Asia to the world, refreshed daily. Request a firm quotation to book.',
  'footer.product': 'Product',
  'footer.rateIndex': 'Rate Index',
  'footer.laneRates': 'Lane rates',
  'footer.requestQuote': 'Request a quote',
  'footer.partners': 'Partners',
  'footer.advertiseList': 'Advertise / list rates',
  'footer.legal': 'Legal',
  'footer.legalBody': 'Rates are indicative market estimates from public sources, not offers. A navo24 product.',
  // Home hero
  'home.badge': 'Live · ex-Asia ocean spot rates',
  'home.h1a': 'The ocean rate,',
  'home.h1b': 'before you ask',
  'home.h1c': '.',
  'home.sub': 'Live spot prices from China and Asia to Europe, the US and worldwide: carrier, transit and cut-offs on one page.',
  'home.subEm': 'Then book through a vetted forwarder.',
  'home.subShort': 'Spot container prices, Asia to anywhere, refreshed daily.',
  'home.indexTapeSub': '40ft HC · median · this week',
  'home.wow': 'w/w',
  // Personal dashboard
  'dash.composite': '40HC composite · all lanes',
  'dash.movers': 'Top movers',
  'dash.priceWow': 'Price · w/w',
  'dash.reliability': 'Reliability',
  'dash.onTime': 'On-time',
  'dash.sailings': 'sailings',
  'dash.relBuilding': 'Tracking on-time…',
  'dash.yourLanes': 'Your lanes',
  'dash.popular': 'Popular lanes',
  'dash.avg': 'avg',
  'dash.recent': 'Recently viewed',
  'dash.pinHint': '★ a lane to pin it here.',
  'dash.pulse': 'This week',
  'dash.pulseIndex': 'ex-Asia spot',
  'dash.leadsFall': 'leads the fall',
  'dash.leadsRise': 'leads the rise',
  'dash.proTitle': 'Watch your lanes',
  'dash.proBody': 'Watch every lane you run and keep the whole list in your account.',
  'dash.proBodyFav': '{n} lanes pinned. Go Pro to watch every one of them.',
  'dash.proCta': 'Go Pro',
  'dash.deskTitle': 'Ask our freight desk',
  'dash.deskBody': "Oversized, reefer, DG, or a lane we don't list? We reply within a working day.",
  'dash.deskCta': 'Ask the desk',
  'dash.trustLive': 'Live from',
  'dash.updated': 'updated',
  'dash.benchmark': 'A public ex-Asia spot benchmark',
  'fav.add': 'Add to your lanes',
  'fav.remove': 'Remove from your lanes',
  'home.ctaQuote': 'Or get a firm all-in quote →',
  'home.ctaIndex': 'View the Rate Index',
  'home.statCarriers': 'carriers',
  'home.statPorts': 'ports',
  'home.statRefresh': 'refresh',
  'home.statRefreshValue': 'Daily',
  'home.indexKicker': 'The Index',
  'home.indexTitle': 'FreightRatesMCP Index',
  'home.indexSub': "Median 40ft (40HC) spot rate by trade lane, from this week's live carrier rates.",
  'home.indexFull': 'Full index →',
  'home.liveKicker': 'Live now',
  'home.liveTitle': 'Latest lane rates',
  'home.allLanes': 'All lanes →',
  'home.loadingRates': 'Loading live rates…',
  'home.lanesShort': 'lanes',
  'home.sailingsShort': 'sailings',
  // RFQ form
  'rfq.title': 'Pre-book this sailing',
  'rfq.sub': 'No card, no commitment yet; usually confirmed within a working day.',
  'rfq.name': 'Your name',
  'rfq.email': 'Work email',
  'rfq.company': 'Company',
  'rfq.origin': 'Origin port',
  'rfq.destination': 'Destination port',
  'rfq.containerType': 'Container type',
  'rfq.readyDate': 'Cargo ready date',
  'rfq.volume': 'Volume / no. of containers',
  'rfq.incoterm': 'Incoterm',
  'rfq.commodity': 'Commodity',
  'rfq.notes': 'Notes',
  'rfq.submit': 'Pre-book this lane',
  'rfq.submitting': 'Reserving…',
  'rfq.success': "Pre-booked. A vetted forwarder will confirm your firm all-in price shortly.",
  'rfq.error': 'Something went wrong. Please try again or email us.',
  'rfq.optional': 'optional',
  // Rate Index page
  'index.h1': 'FreightRatesMCP Index',
  'index.intro': 'A weekly benchmark of ocean spot freight rates out of Asia, by trade lane. Figures are the median of live carrier rates we refresh daily; indicative, not a firm quotation.',
  'index.th.lane': 'Trade lane',
  'index.th.median': 'Median 40HC',
  'index.th.avg20': 'Avg 20ft',
  'index.th.range': 'Range 40HC',
  'index.th.transit': 'Avg transit',
  'index.th.lanes': 'Lanes',
  'index.methodPre': 'Methodology: the median and average of publicly listed carrier spot rates per trade lane, refreshed daily. Historical series and per-lane charts are available via the API (see',
  'index.methodLink': 'Pricing',
  // Lanes hub
  'lanes.h1': 'Freight rates by lane',
  'lanes.intro': 'Live ocean spot rates from Asia to the world. Click any card for the full sailing schedule, routing and a booking quote.',
  'lanes.all': 'All',
  'lanes.loading': 'Loading lanes…',
  // Trade page
  'trade.crumb': 'Rate Index',
  'trade.lanesOn': 'Lanes on this trade',
  'trade.unknown': 'Unknown trade lane.',
  'trade.seeIndex': 'See the index →',
  'trade.stat.median': 'Median 40HC',
  'trade.stat.avg20': 'Avg 20ft',
  'trade.stat.transit': 'Avg transit',
  'trade.stat.lanes': 'Lanes',
  'trade.suffix': 'freight rates',
  // Lane page
  'lane.crumbLanes': 'Lanes',
  'lane.rateSuffix': 'freight rate',
  'lane.book': 'Pre-book this lane',
  'lane.bookSub': 'The rate above is indicative; pre-book and a vetted forwarder confirms a firm all-in price.',
  'lane.getQuote': 'Get a quote →',
  'lane.loading': 'Loading lane…',
  'lane.daysTransit': 'days transit',
  'lane.sailingsWord': 'live sailings',
  'lane.indicativeSpot': 'indicative spot rate',
  'lane.updated': 'updated',
  'lane.yourPick': 'Your pick',
  'lane.base': 'base',
  'lane.indicative': 'Indicative market rate from public carrier listings.',
  'lane.requestQuote': 'Request a firm all-in quote →',
  // Lane enrichment (data-driven; {origin}{destination}{n}{carriers}{tmin}{tmax}{cheapest40}{cheapestCarrier}{fastest}{p20}{p40}{transit}{carrierList}{pct}{carrier}{obs})
  'lane.schedule': 'Sailing schedule',
  'lane.about': 'About this route',
  'lane.aboutBody': 'Rates on the {origin} → {destination} lane are live carrier spot prices from public listings. They move week to week with capacity, fuel and demand, so we refresh them daily to help you time your booking. For a firm, all-in price including local charges and surcharges, request a quote and we match your shipment to a vetted forwarder.',
  'lane.faqTitle': 'Frequently asked questions',
  'lane.otherRoutes': 'Other routes from {origin}',
  'lane.stats': '{n} sailings · {carriers} carriers · transit {tmin}–{tmax} days',
  'lane.narrative': 'This week there are {n} sailings from {origin} to {destination} across {carriers} carriers. The lowest 40ft HC spot rate is {cheapest40} with {cheapestCarrier}, and the fastest port-to-port transit is {fastest} days. {routing}',
  'lane.routingDirect': 'Most services sail direct.',
  'lane.routingTransship': 'Some services route via a transshipment hub, which can add a few days.',
  'lane.th.carrier': 'Carrier',
  'lane.th.vessel': 'Vessel',
  'lane.th.voyage': 'Voyage',
  'lane.th.service': 'Service',
  'lane.th.etd': 'ETD',
  'lane.th.transit': 'Transit',
  'lane.faq.q1': 'How much does it cost to ship a container from {origin} to {destination}?',
  'lane.faq.a1': 'The current ocean spot rate from {origin} to {destination} is {p40} for a 40ft HC container and {p20} for a 20ft, updated daily. Request a firm all-in quote for your exact shipment.',
  'lane.faq.q2': 'How long does shipping from {origin} to {destination} take?',
  'lane.faq.a2': 'Ocean transit from {origin} to {destination} takes around {transit} days port to port, depending on the carrier and service.',
  'lane.faq.q3': 'Which carriers ship from {origin} to {destination}?',
  'lane.faq.a3': 'Carriers currently sailing this route include {carrierList}. Schedules and rates are refreshed daily.',
  'lane.faq.q4': 'How often do ships depart from {origin} to {destination}?',
  'lane.faq.a4': 'We track {n} live sailings on the {origin} → {destination} lane, with multiple departures every week.',
  'lane.reliabilityLine': 'Best tracked on-time performance: {pct}% ({carrier}, {obs} sailings).',
  'lane.relTitle': 'On-time reliability',
  'lane.relSource': 'from tracked sailings',
  'lane.relCoverage': 'We track {vessels} vessels from {lines} lines on this route.',
  'lane.relAnalyzed': 'We analysed {obs} recent sailings across {carriers} carriers for on-time performance.',
  'lane.relBasedOn': 'last {n} sailings',
  'lane.relAlso': 'Also sailing',
  'lane.relTracking': 'on-time tracking',
  'lane.relBuilding': 'Building on-time history for this lane; check back as sailings accumulate.',
  'trend.title': 'Rate trend',
  'trend.building': 'Building history. We snapshot this lane daily. The trend chart appears once there are a few days of data.',
  'trend.today': 'Today',
  'trend.medianDays': '40ft HC · median · {n} days',
  // Rate card
  'card.transship': 'transship',
  'card.direct': 'direct',
  'card.days': 'days',
  'card.vessel': 'Vessel',
  'card.voyage': 'Voyage',
  'card.service': 'Service',
  // Lane search (hero)
  'search.container': 'Container',
  'search.from': 'From',
  'search.to': 'To',
  'search.loadingPorts': 'Loading ports…',
  'search.originPh': 'Shanghai…',
  'search.destPh': 'Rotterdam…',
  'search.anyDest': 'Anywhere',
  'search.swap': 'Swap origin and destination',
  'search.check': 'Check rate →',
  'search.getQuote': 'Get a quote →',
  'search.noRate': "No published rate on this exact lane yet; we'll get you a firm quote for it.",
  'search.popular': 'Popular',
  'search.shippingTo': 'Shipping to',
  // Common
  'common.getQuote': 'Get a quote →',
  'common.perContainer': 'per container',
  'common.transit': 'transit',
  'common.days': 'days',
  'common.viewInLang': 'View this page in English',
  // Page meta (templated: {name} {blurb} {pol} {pod} {polCode} {podCode} {p20} {p40} {transit} {carrier} {price})
  'meta.home.title': 'FreightRatesMCP — Ocean Freight Rates from Asia, Updated Daily',
  'meta.home.desc': 'Check ocean spot freight rates from China and Asia to Europe, the US and worldwide: 20ft & 40ft container prices, transit times and reliability, updated daily. Request a booking in one click.',
  'meta.index.title': 'FreightRatesMCP Index — Ocean Spot Freight Rate Benchmark by Trade Lane',
  'meta.index.desc': 'The FreightRatesMCP Index: weekly median ocean spot freight rates from Asia by trade lane, covering Asia–Europe, Asia–US, Asia–Latin America and more. 20ft & 40ft, updated daily.',
  'meta.lanes.title': 'Ocean Freight Rates by Lane — China & Asia Export Spot Rates | FreightRatesMCP',
  'meta.lanes.desc': 'Browse live ocean spot freight rates by lane from China and Asia to ports worldwide: 20ft & 40ft container prices, transit times and carriers, updated daily.',
  'meta.book.title': 'Get a Freight Quote — Book Ocean Shipping from Asia | FreightRatesMCP',
  'meta.book.desc': 'Request a firm, all-in ocean freight quotation from Asia to your port. No cost, no obligation; we pass your enquiry to a vetted forwarder.',
  'meta.trade.title': '{name} Freight Rates — Spot Container Prices | FreightRatesMCP',
  'meta.trade.desc': '{blurb} 20ft & 40ft ocean spot rates, transit times and carriers, updated daily.',
  'meta.lane.title': '{pol} to {pod} Freight Rate — {price}/40ft',
  'meta.lane.desc': 'Ocean spot freight rate from {pol} ({polCode}) to {pod} ({podCode}): 20ft {p20}, 40HC {p40}, ~{transit} days on {carrier}. Sailing schedule, routing, cutoffs, updated daily.',
  'meta.lane.titleEmpty': 'Ocean Freight Rate — FreightRatesMCP',
  'meta.lane.descEmpty': 'Ocean spot freight rate by lane, updated daily.',
  'meta.lane.carriersFallback': 'major carriers',
  // Country landing (English fallback — not rendered at root in phase 1, but keeps t() safe)
  'meta.country.title': 'Ocean Freight Rates from Asia — Container Spot Prices, Updated Daily | FreightRatesMCP',
  'meta.country.desc': 'Live ocean freight rates from China and Asia: 20ft & 40ft container spot prices with transit time, carrier and cut-offs. Updated daily; firm quote in one click.',
  'country.badge': 'Ocean freight rates · Asia → Europe',
  'country.h1': 'Ocean freight rates from Asia',
  'country.sub': 'Live spot rates from China and Asia to European ports and their gateways.',
  'country.body1': 'FreightRatesMCP pools the current spot rates of the major carriers from the main Asian ports (Shanghai, Ningbo, Shenzhen, Qingdao), so you can see price, transit time and carrier at a glance before you book.',
  'country.body2': 'All rates are indicative market values for 20ft (20GP), 40ft (40GP) and 40ft High Cube (40HC) containers, refreshed daily from public carrier listings. For a firm, immediately bookable all-in rate, request a quote; we match your shipment to a vetted forwarder, usually within a working day.',
  'country.lanesTitle': 'Popular routes',
}

const de = {
  'nav.rateIndex': 'Frachtraten-Index',
  'nav.lanes': 'Routen',
  'nav.advertise': 'Werben',
  'nav.pricing': 'Preise',
  'nav.signIn': 'Anmelden',
  'nav.account': 'Konto',
  'nav.getQuote': 'Angebot anfordern',
  'nav.language': 'Sprache',
  'footer.tagline': 'Indikative Seefracht-Spotraten von Asien in alle Welt, täglich aktualisiert. Für die Buchung ein verbindliches Angebot anfordern.',
  'footer.product': 'Produkt',
  'footer.rateIndex': 'Frachtraten-Index',
  'footer.laneRates': 'Routenraten',
  'footer.requestQuote': 'Angebot anfordern',
  'footer.partners': 'Partner',
  'footer.advertiseList': 'Werben / Raten listen',
  'footer.legal': 'Rechtliches',
  'footer.legalBody': 'Die Raten sind indikative Marktschätzungen aus öffentlichen Quellen, keine Angebote. Eine Marke von navo24.',
  'home.badge': 'Täglich aktualisiert · indikative Spotraten',
  'home.h1a': 'Was kostet der Seetransport aus',
  'home.h1b': 'Asien',
  'home.h1c': 'diese Woche?',
  'home.sub': 'Aktuelle Seefracht-Spotraten von China und Asien nach Europa, in die USA und weltweit — Reederei, Laufzeit und Cut-offs auf einer Seite. Anschließend über einen geprüften Spediteur buchen.',
  'home.ctaQuote': 'Oder ein verbindliches All-in-Angebot anfordern →',
  'home.ctaIndex': 'Zum Frachtraten-Index',
  'home.statCarriers': 'Reedereien',
  'home.statPorts': 'Häfen',
  'home.statRefresh': 'Aktualisierung',
  'home.statRefreshValue': 'Täglich',
  'home.indexKicker': 'Der Index',
  'home.indexTitle': 'FreightRatesMCP Index',
  'home.indexSub': 'Median-Spotrate für 40ft (40HC) je Handelsroute, aus den Live-Raten der Reedereien dieser Woche.',
  'home.indexFull': 'Vollständiger Index →',
  'home.liveKicker': 'Jetzt live',
  'home.liveTitle': 'Aktuelle Routenraten',
  'home.allLanes': 'Alle Routen →',
  'home.loadingRates': 'Live-Raten werden geladen…',
  'home.lanesShort': 'Routen',
  'home.sailingsShort': 'Abfahrten',
  'rfq.title': 'Verbindliches Frachtangebot anfordern',
  'rfq.sub': 'Nennen Sie uns Ihre Sendung; wir vermitteln sie an einen geprüften Spediteur für einen verbindlichen All-in-Preis.',
  'rfq.name': 'Ihr Name',
  'rfq.email': 'Geschäftliche E-Mail',
  'rfq.company': 'Firma',
  'rfq.origin': 'Abgangshafen',
  'rfq.destination': 'Zielhafen',
  'rfq.containerType': 'Containertyp',
  'rfq.readyDate': 'Bereitstellungsdatum der Ware',
  'rfq.volume': 'Volumen / Anzahl Container',
  'rfq.incoterm': 'Incoterm',
  'rfq.commodity': 'Warenart',
  'rfq.notes': 'Anmerkungen',
  'rfq.submit': 'Angebot anfordern',
  'rfq.submitting': 'Wird gesendet…',
  'rfq.success': 'Vielen Dank — Ihre Anfrage liegt uns vor. Wir vermitteln Sie in Kürze an einen Spediteur.',
  'rfq.error': 'Etwas ist schiefgelaufen. Bitte erneut versuchen oder uns eine E-Mail schreiben.',
  'rfq.optional': 'optional',
  'index.h1': 'FreightRatesMCP Index',
  'index.intro': 'Ein wöchentlicher Richtwert für Seefracht-Spotraten ab Asien, nach Handelsroute. Die Werte sind der Median der Live-Reedereiraten, die wir täglich aktualisieren — indikativ, kein verbindliches Angebot.',
  'index.th.lane': 'Handelsroute',
  'index.th.median': 'Median 40HC',
  'index.th.avg20': 'Ø 20ft',
  'index.th.range': 'Spanne 40HC',
  'index.th.transit': 'Ø Laufzeit',
  'index.th.lanes': 'Routen',
  'index.methodPre': 'Methodik: Median und Durchschnitt der öffentlich gelisteten Reederei-Spotraten je Handelsroute, täglich aktualisiert. Historische Reihen und Charts je Route gibt es über die API (siehe',
  'index.methodLink': 'Preise',
  'lanes.h1': 'Frachtraten nach Route',
  'lanes.intro': 'Aktuelle Seefracht-Spotraten von Asien in alle Welt. Klicken Sie auf eine Karte für den vollständigen Fahrplan, das Routing und ein Buchungsangebot.',
  'lanes.all': 'Alle',
  'lanes.loading': 'Routen werden geladen…',
  'trade.crumb': 'Frachtraten-Index',
  'trade.lanesOn': 'Routen in diesem Verkehr',
  'trade.unknown': 'Unbekannte Handelsroute.',
  'trade.seeIndex': 'Zum Index →',
  'trade.stat.median': 'Median 40HC',
  'trade.stat.avg20': 'Ø 20ft',
  'trade.stat.transit': 'Ø Laufzeit',
  'trade.stat.lanes': 'Routen',
  'trade.suffix': 'Frachtraten',
  'lane.crumbLanes': 'Routen',
  'lane.rateSuffix': 'Frachtrate',
  'lane.book': 'Diese Route buchen',
  'lane.bookSub': 'Ein verbindliches All-in-Angebot von einem geprüften Spediteur — in der Regel innerhalb eines Werktags.',
  'lane.getQuote': 'Angebot anfordern →',
  'lane.loading': 'Route wird geladen…',
  'lane.daysTransit': 'Tage Laufzeit',
  'lane.sailingsWord': 'Live-Abfahrten',
  'lane.indicativeSpot': 'indikative Spotrate',
  'lane.updated': 'aktualisiert',
  'lane.yourPick': 'Ihre Wahl',
  'lane.base': 'Basis',
  'lane.indicative': 'Indikative Marktrate aus öffentlichen Reederei-Listen.',
  'lane.requestQuote': 'Verbindliches All-in-Angebot anfordern →',
  'lane.schedule': 'Fahrplan',
  'lane.about': 'Über diese Route',
  'lane.aboutBody': 'Die Raten auf der Route {origin} → {destination} sind Live-Spotpreise der Reedereien aus öffentlichen Listen. Sie schwanken von Woche zu Woche mit Kapazität, Bunkerpreisen und Nachfrage — deshalb aktualisieren wir sie täglich, damit Sie Ihre Buchung optimal timen können. Für einen festen All-in-Preis inklusive lokaler Gebühren und Zuschläge fordern Sie ein Angebot an; wir vermitteln Ihre Sendung an einen geprüften Spediteur.',
  'lane.faqTitle': 'Häufige Fragen',
  'lane.otherRoutes': 'Weitere Routen ab {origin}',
  'lane.stats': '{n} Abfahrten · {carriers} Reedereien · Laufzeit {tmin}–{tmax} Tage',
  'lane.narrative': 'Diese Woche gibt es {n} Abfahrten von {origin} nach {destination} über {carriers} Reedereien. Die günstigste 40ft-HC-Spotrate liegt bei {cheapest40} mit {cheapestCarrier}, die schnellste Laufzeit von Hafen zu Hafen beträgt {fastest} Tage. {routing}',
  'lane.routingDirect': 'Die meisten Dienste fahren direkt.',
  'lane.routingTransship': 'Einige Dienste laufen über einen Umschlaghafen, was einige Tage zusätzlich bedeuten kann.',
  'lane.th.carrier': 'Reederei',
  'lane.th.vessel': 'Schiff',
  'lane.th.voyage': 'Reise',
  'lane.th.service': 'Dienst',
  'lane.th.etd': 'Abfahrt',
  'lane.th.transit': 'Laufzeit',
  'lane.faq.q1': 'Was kostet der Containertransport von {origin} nach {destination}?',
  'lane.faq.a1': 'Die aktuelle Seefracht-Spotrate von {origin} nach {destination} beträgt {p40} für einen 40ft-HC- und {p20} für einen 20ft-Container, täglich aktualisiert. Fordern Sie ein verbindliches All-in-Angebot für Ihre konkrete Sendung an.',
  'lane.faq.q2': 'Wie lange dauert der Transport von {origin} nach {destination}?',
  'lane.faq.a2': 'Die Seelaufzeit von {origin} nach {destination} beträgt rund {transit} Tage von Hafen zu Hafen, je nach Reederei und Dienst.',
  'lane.faq.q3': 'Welche Reedereien fahren von {origin} nach {destination}?',
  'lane.faq.a3': 'Zu den Reedereien auf dieser Route zählen derzeit {carrierList}. Fahrpläne und Raten werden täglich aktualisiert.',
  'lane.faq.q4': 'Wie oft fahren Schiffe von {origin} nach {destination}?',
  'lane.faq.a4': 'Wir erfassen {n} Live-Abfahrten auf der Route {origin} → {destination}, mit mehreren Abfahrten pro Woche.',
  'lane.reliabilityLine': 'Beste erfasste Pünktlichkeit: {pct}% ({carrier}, {obs} Abfahrten).',
  'trend.title': 'Ratenverlauf',
  'trend.building': 'Historie wird aufgebaut — wir erfassen diese Route täglich. Das Verlaufsdiagramm erscheint, sobald einige Tage an Daten vorliegen.',
  'trend.today': 'Heute',
  'trend.medianDays': '40ft HC · Median · {n} Tage',
  'card.transship': 'Umladung',
  'card.direct': 'direkt',
  'card.days': 'Tage',
  'card.vessel': 'Schiff',
  'card.voyage': 'Reise',
  'card.service': 'Service',
  'search.container': 'Container',
  'search.from': 'Von',
  'search.to': 'Nach',
  'search.loadingPorts': 'Häfen werden geladen…',
  'search.originPh': 'Shanghai, Ningbo, Shekou…',
  'search.destPh': 'Rotterdam, Hamburg…',
  'search.anyDest': 'Beliebiges Ziel',
  'search.swap': 'Start und Ziel tauschen',
  'search.check': 'Rate prüfen →',
  'search.getQuote': 'Angebot anfordern →',
  'search.noRate': 'Für genau diese Route liegt noch keine veröffentlichte Rate vor — wir besorgen Ihnen ein verbindliches Angebot.',
  'search.popular': 'Beliebt',
  'search.shippingTo': 'Versand nach',
  'common.getQuote': 'Angebot anfordern →',
  'common.perContainer': 'pro Container',
  'common.transit': 'Laufzeit',
  'common.days': 'Tage',
  'common.viewInLang': 'Diese Seite auf Deutsch ansehen',
  'meta.home.title': 'FreightRatesMCP — Seefrachtraten aus Asien, täglich aktualisiert',
  'meta.home.desc': 'Seefracht-Spotraten von China und Asien nach Europa, in die USA und weltweit prüfen — Preise für 20ft- & 40ft-Container, Laufzeiten und Zuverlässigkeit, täglich aktualisiert. Buchung mit einem Klick anfragen.',
  'meta.index.title': 'FreightRatesMCP Index — Seefracht-Spotraten-Benchmark nach Handelsroute',
  'meta.index.desc': 'Der FreightRatesMCP Index: wöchentliche Median-Seefrachtraten ab Asien nach Handelsroute — Asien–Europa, Asien–USA, Asien–Lateinamerika und mehr. 20ft & 40ft, täglich aktualisiert.',
  'meta.lanes.title': 'Seefrachtraten nach Route — Spotraten für Exporte aus China & Asien | FreightRatesMCP',
  'meta.lanes.desc': 'Aktuelle Seefracht-Spotraten nach Route von China und Asien zu Häfen weltweit durchsuchen — Preise für 20ft- & 40ft-Container, Laufzeiten und Reedereien, täglich aktualisiert.',
  'meta.book.title': 'Frachtangebot anfordern — Seefracht aus Asien buchen | FreightRatesMCP',
  'meta.book.desc': 'Fordern Sie ein verbindliches All-in-Seefrachtangebot von Asien zu Ihrem Hafen an. Kostenlos und unverbindlich — wir vermitteln Ihre Sendung an einen geprüften Spediteur.',
  'meta.trade.title': '{name} — Seefracht-Spotpreise | FreightRatesMCP',
  'meta.trade.desc': '{blurb} 20ft- & 40ft-Seefracht-Spotraten, Laufzeiten und Reedereien, täglich aktualisiert.',
  'meta.lane.title': 'Seefrachtrate {pol} → {pod} — {price}/40ft',
  'meta.lane.desc': 'Seefracht-Spotrate von {pol} ({polCode}) nach {pod} ({podCode}): 20ft {p20}, 40HC {p40}, ~{transit} Tage mit {carrier}. Fahrplan, Routing, Cut-offs — täglich aktualisiert.',
  'meta.lane.titleEmpty': 'Seefrachtrate — FreightRatesMCP',
  'meta.lane.descEmpty': 'Seefracht-Spotrate nach Route, täglich aktualisiert.',
  'meta.lane.carriersFallback': 'großen Reedereien',
  'meta.country.title': 'Seefrachtraten Asien → Deutschland — Container-Spotpreise, täglich aktualisiert | FreightRatesMCP',
  'meta.country.desc': 'Aktuelle Seefrachtraten von China und Asien nach Deutschland: Spotpreise für 20ft- & 40ft-Container nach Hamburg, Bremerhaven, Rotterdam und Antwerpen — mit Laufzeit, Reederei und Cut-offs. Täglich aktualisiert, verbindliches Angebot in einem Klick.',
  'country.badge': 'Seefrachtraten · Asien → Deutschland',
  'country.h1': 'Seefrachtraten von Asien nach Deutschland',
  'country.sub': 'Aktuelle Spotraten von China und Asien zu deutschen Häfen und ihren Gateways — Hamburg, Bremerhaven sowie Rotterdam und Antwerpen.',
  'country.body1': 'Was der Seetransport eines Containers von China nach Deutschland kostet, ändert sich Woche für Woche. FreightRatesMCP bündelt die aktuellen Spotraten der großen Reedereien für die wichtigsten Asien-Häfen — Shanghai, Ningbo, Shenzhen, Qingdao — nach Hamburg, Bremerhaven sowie über die Nordrange-Gateways Rotterdam und Antwerpen, über die ein großer Teil der deutschen Importe läuft. So sehen Sie auf einen Blick Preis, Laufzeit und Reederei, bevor Sie buchen.',
  'country.body2': 'Alle Raten sind indikative Marktwerte für 20ft- (20GP), 40ft- (40GP) und 40ft-High-Cube-Container (40HC), täglich aus öffentlichen Reederei-Listen aktualisiert. Zusätzlich zeigen wir, wo verfügbar, die beobachtete Fahrplan-Pünktlichkeit je Reederei — damit Sie nicht nur den günstigsten, sondern den zuverlässigsten Dienst wählen. Für eine feste, sofort buchbare All-in-Rate fordern Sie ein Angebot an: Wir vermitteln Ihre Sendung an einen geprüften Spediteur, in der Regel innerhalb eines Werktags.',
  'country.lanesTitle': 'Beliebte Routen nach Deutschland',
}

// Base catalog = English + German (inline above). The other 23 native locales live in data.extra.mjs
// (~347KB) and are loaded LAZILY: English/German visitors — the majority — never download it. The
// prerender awaits loadExtraLocales() to emit every localized page; the client calls it only when a
// non-base locale is actually active. Both merge into these SAME exported objects (the i18n runtime
// holds a live reference to CATALOG), so a re-render after the load surfaces the native strings. A
// locale still missing its catalog degrades to English in t() — safe.
export const CATALOG = { en, de }
export const TRADES_I18N = { ...TRADES_BASE }

let extraPromise
export function loadExtraLocales() {
  if (!extraPromise) {
    extraPromise = import('./data.extra.mjs').then(({ EXTRA }) => {
      for (const [code, e] of Object.entries(EXTRA)) {
        if (e?.catalog) CATALOG[code] = e.catalog
        if (e?.trades) TRADES_I18N[code] = e.trades
      }
    })
  }
  return extraPromise
}
